/**
 * Purpose:
 *   Toast notification component for displaying error, warning, and success messages.
 *
 * Notes:
 *   - Uses the "common" i18n namespace for button labels.
 *   - Pure presentational component - receives all state via props
 *   - A11y: error -> role="alert" + aria-live="assertive", others -> role="status" + aria-live="polite"
 *   - Progress: bottom 2px rule animated from 100% → 0%
 *   - Respects prefers-reduced-motion via CSS
 */

'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/util/functions/cx';
import type { ToastType } from '../context/ToastContext';
import { toastStyles } from '../styles/Toast.styles';
import '../styles/style.css';

export interface IToastViewProps {
  /** Message body displayed inside the toast. */
  message: string;
  /** Visual + a11y behavior. 'error' -> alert/assertive, others -> status/polite. */
  type: ToastType; // 'error' | 'warning' | 'success'
  /** Remaining duration (ms). Drives the bottom progress rule. */
  progressMs: number;
  /** Pause progress animation (true while hovering). */
  paused?: boolean;
  /** Called when the user clicks the close button. */
  onClose: () => void;
}

export const Toast: React.FC<IToastViewProps> = ({
  message,
  type,
  progressMs,
  paused,
  onClose,
}) => {
  const { t } = useTranslation('common');
  const isError = type === 'error';
  const role = isError ? 'alert' : 'status';
  const liveProps = isError
    ? { 'aria-live': 'assertive' as const }
    : { 'aria-live': 'polite' as const };

  const variant = toastStyles.variant[type];
  const dotVariant = toastStyles.dot[type];
  const closeLabel = t('toast.close_notification');

  /**
   * Progress rule style
   * -------------------
   * React 19 warns if you mix the 'animation' shorthand with longhands like
   * 'animationPlayState'. To avoid the warning, we specify *only longhands*.
   */
  const clamped = Math.max(progressMs, 0);
  const progressStyle: React.CSSProperties = {
    transformOrigin: 'left',
    animationName: 'toast-progress-shrink',
    animationDuration: `${clamped}ms`,
    animationTimingFunction: 'linear',
    animationFillMode: 'forwards',
    animationPlayState: paused ? 'paused' : 'running',
  };

  return (
    <div role={role} {...liveProps} className={cx(toastStyles.base, variant)}>
      {/* Variant dot (decorative) */}
      <span aria-hidden className={cx(toastStyles.dot.base, dotVariant)} />

      {/* Message */}
      <div className={toastStyles.message}>
        <p className={toastStyles.messageText}>{message}</p>
      </div>

      {/* Close button (keyboard/accessibility friendly) */}
      <button
        type="button"
        onClick={onClose}
        className={toastStyles.closeButton}
        aria-label={closeLabel}
      >
        <span aria-hidden>×</span>
      </button>

      {/* Bottom progress rule */}
      <div
        aria-hidden
        className={cx(toastStyles.progressBar, variant)}
        style={progressStyle}
      />
    </div>
  );
};
